import { Component, OnInit, OnDestroy } from '@angular/core';
import { CartService } from 'src/app/services/cart.service';
import { AppliedCoupon } from 'src/app/models/applied-coupon.model';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, switchMap, debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-coupons',
  templateUrl: './coupons.component.html',
  styleUrl: './coupons.component.css'
})
export class CouponsComponent implements OnInit, OnDestroy {
  // Observables moved from CartComponent
  visibleCouponsWithEligibility$: Observable<({ coupon: AppliedCoupon, isEligible: boolean })[]>;
  currentlyAppliedCoupon$: Observable<AppliedCoupon | null>;

  public cartService: CartService;

  // Alert state
  public showAlert: boolean = false;
  public alertMessage: string = '';
  public alertType: 'success' | 'danger' | 'info' | 'warning' = 'danger';

  // Manual input
  public manualCouponCode: string = '';

  private subscriptions: Subscription = new Subscription();

  constructor(
    cartService: CartService,
  ) {
    this.cartService = cartService;

    // Initialize observables
    this.currentlyAppliedCoupon$ = this.cartService.getCurrentlyAppliedCoupon();
    this.visibleCouponsWithEligibility$ = of([]); // Initial value
  }

  ngOnInit(): void {
    this.resetAlert();

    // Re-implement coupon eligibility logic here
    this.visibleCouponsWithEligibility$ = combineLatest([
      this.cartService.getVisibleCoupons(),
      this.cartService.cartTotal$.pipe(debounceTime(100), distinctUntilChanged()),
      this.cartService.appliedCoupons$.pipe(distinctUntilChanged((prev, curr) => prev.length === curr.length && prev[0]?.coupon_code === curr[0]?.coupon_code))
    ]).pipe(
      // Ensure we switch to a new check whenever one of the source observables emits
      switchMap(([coupons, cartTotal, appliedCoupons]) => {
        if (coupons.length === 0) {
          return of([]);
        }
        // Map each coupon to an eligibility check observable
        const eligibilityChecks = coupons.map(coupon =>
          this.cartService.isCouponEligible(coupon).pipe(
            map(isEligible => ({ coupon, isEligible }))
          )
        );
        // Wait for all eligibility checks to complete
        return combineLatest(eligibilityChecks);
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  // Method for applying or removing a coupon (Re-implemented)
  onApplyOrRemoveCoupon(couponCode: string, isEligible: boolean): void {
    this.resetAlert();
    const isCurrentlyApplied = this.cartService.isCouponActive(couponCode);

    if (isCurrentlyApplied) {
      this.cartService.removeCoupon(couponCode);
      this.setAlert(`Coupon "${couponCode}" has been removed.`, 'warning');
    } else if (isEligible) {
      const result = this.cartService.applyCouponByCode(couponCode);
      if (result.success) {
        this.setAlert(result.message, 'success');
      } else {
        this.setAlert(result.message, 'danger');
      }
    } else {
      this.setAlert('This coupon is not eligible for your current cart.', 'info');
    }
  }

  // Method for copying the coupon code (Re-implemented)
  onCopyCouponCode(couponCode: string): void {
    const couponElement = document.getElementById('couponCode_' + couponCode) as HTMLInputElement;
    if (couponElement) {
      try {
        navigator.clipboard.writeText(couponCode);
        this.setAlert(`Coupon code "${couponCode}" copied to clipboard!`, 'success');
      } catch (err) {
        this.setAlert('Failed to copy. Please copy manually.', 'danger');
        console.error('Clipboard API write failed:', err);
      }
    } else {
      this.setAlert('Failed to copy coupon code.', 'danger');
    }
  }

  // Method to apply a manually entered coupon (Re-implemented)
  applyManualCoupon(): void {
    this.resetAlert();
    if (!this.manualCouponCode || this.manualCouponCode.trim() === '') {
      this.setAlert('Please enter a coupon code.', 'danger');
      return;
    }

    this.currentlyAppliedCoupon$.pipe(distinctUntilChanged(), map(c => c?.coupon_code.toUpperCase()), switchMap(currentCode => {
      const newCouponCode = this.manualCouponCode.trim().toUpperCase();
      const isAlreadyApplied = currentCode === newCouponCode;

      if (isAlreadyApplied) {
        this.setAlert(`Coupon "${currentCode}" is already applied.`, 'info');
        return of(null); // Stop the flow
      }

      if (currentCode) {
        this.setAlert(`"${currentCode}" is currently applied. Attempting to apply "${newCouponCode}" instead.`, 'info');
      }

      const result = this.cartService.applyCouponByCode(this.manualCouponCode.trim());
      if (result.success) {
        this.setAlert(result.message, 'success');
        this.manualCouponCode = '';
      } else {
        this.setAlert(result.message, 'danger');
      }
      return of(null); // Stop the flow
    })).subscribe();
  }


  // Alert methods (Re-implemented)
  setAlert(message: string, type: 'success' | 'danger' | 'info' | 'warning'): void {
    this.alertMessage = message;
    this.alertType = type;
    this.showAlert = true;

    setTimeout(() => {
      this.resetAlert();
    }, 5000);
  }

  resetAlert(): void {
    this.showAlert = false;
    this.alertMessage = '';
    this.alertType = 'danger';
  }
}